import { addDays, format, isSameDay, type Locale, startOfWeek } from 'date-fns';
import { type Slot } from '../services/appointment/model';

export interface Weekday {
  name: string;
  title: string;
}

export interface CalendarDay {
  date: string;
  slot: Slot | undefined;
  replacementServiceSlot: Slot | undefined;
}

const DAYS_IN_WEEK = 7;
const WEEKDAYS_ARRAY: Weekday[] = Array(DAYS_IN_WEEK).fill({
  name: '',
  title: '',
});

export const getWeekdays = (
  calendarDays: CalendarDay[],
  locale: Locale,
): Weekday[] => {
  return WEEKDAYS_ARRAY.map((_, index) => {
    const name = format(calendarDays[index].date, 'EEE', {
      locale,
    });
    return {
      name: `${name[0].toUpperCase()}${name.substring(1)}`,
      title: format(calendarDays[index].date, 'EEEE', {
        locale,
      }),
    };
  });
};

export const getCalendarDayByDate = (
  calendarDays: CalendarDay[],
  date: string,
): CalendarDay | undefined => {
  return calendarDays.find((day) => isSameDay(day.date, date));
};

export const getCalendarDays = (
  startDate: string,
  weekStartsOn: 0 | 1,
  numberOfWeeks: number,
  slots: Slot[],
  replacementServiceSlots: Slot[],
  replacementServiceSlotsEnabled: boolean,
): CalendarDay[] => {
  const firstDateOfWeek = startOfWeek(startDate, {
    weekStartsOn: weekStartsOn,
  });

  return Array(numberOfWeeks * DAYS_IN_WEEK)
    .fill(null)
    .map((_, n) => {
      const date = addDays(firstDateOfWeek, n).toISOString();
      const slot = slots.find((nextSlot) => isSameDay(nextSlot.date, date));
      const replacementServiceSlot = replacementServiceSlotsEnabled
        ? replacementServiceSlots.find((nextSlot) =>
            isSameDay(nextSlot.date, date),
          )
        : undefined;

      return {
        date,
        slot,
        replacementServiceSlot,
      };
    });
};

export const getFirstAvailableSlot = (
  calendarDays: CalendarDay[],
): { slot?: Slot; isReplacementService: boolean } => {
  const calendarDayWithSlot = calendarDays.find((day) => day.slot);
  const calendarDayWithReplacementServiceSlot = calendarDays.find(
    (day) => day.replacementServiceSlot,
  );

  if (calendarDayWithSlot) {
    return { slot: calendarDayWithSlot.slot, isReplacementService: false };
  } else if (calendarDayWithReplacementServiceSlot) {
    return {
      slot: calendarDayWithReplacementServiceSlot.replacementServiceSlot,
      isReplacementService: true,
    };
  }
  return { slot: undefined, isReplacementService: false };
};

export const getFirstAvailableDate = (calendarDays: CalendarDay[]): string => {
  const { slot } = getFirstAvailableSlot(calendarDays);
  return slot?.date || '';
};
